'use client'
import React from 'react'
import {
  Divider,
  IconButton,
  TextField,
  Typography,
  Grid
} from '@mui/material'
import { X } from 'lucide-react'
import { IShippingDetails } from '@/lib/interfaces/ordering'
import { Language } from '@/lib/interfaces/interfaces'

interface ShippingDetailsProps {
  shippingDetails: IShippingDetails
  setShippingDetails: React.Dispatch<React.SetStateAction<IShippingDetails>>
  toggleCart: () => void
  errors?: { [key: string]: string }
  language: Language
}

// Objeto de traducciones para cada idioma
const translations = {
  es: {
    shippingTitle: 'Datos de Envío',
    closeCart: 'Cerrar carrito',
    firstName: 'Nombre',
    lastName: 'Apellido',
    address: 'Dirección',
    address2: 'Dirección 2 (opcional)',
    city: 'Ciudad',
    postalCode: 'Código Postal',
    provinceState: 'Provincia/Estado',
    phone: 'Teléfono',
    additionalInfo: 'Información Adicional'
  },
  en: {
    shippingTitle: 'Shipping Details',
    closeCart: 'Close cart',
    firstName: 'First Name',
    lastName: 'Last Name',
    address: 'Address',
    address2: 'Address 2 (optional)',
    city: 'City',
    postalCode: 'Postal Code',
    provinceState: 'Province/State',
    phone: 'Phone',
    additionalInfo: 'Additional Information'
  },
  fr: {
    shippingTitle: 'Détails de livraison',
    closeCart: 'Fermer le panier',
    firstName: 'Prénom',
    lastName: 'Nom',
    address: 'Adresse',
    address2: 'Adresse 2 (facultatif)',
    city: 'Ville',
    postalCode: 'Code Postal',
    provinceState: 'Province/État',
    phone: 'Téléphone',
    additionalInfo: 'Informations supplémentaires'
  }
}

const ShippingDetails = ({
  shippingDetails,
  setShippingDetails,
  toggleCart,
  errors,
  language
}: ShippingDetailsProps) => {
  const texts = translations[language] || translations.es

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setShippingDetails(prev => ({
      ...prev,
      [name]: value
    }))
  }

  return (
    <>
      {/* Encabezado similar al de CartList */}
      <div className='orderingCart__container__header'>
        <Typography variant='h6' className='orderingCart__container__header__title'>
          {texts.shippingTitle}
        </Typography>
        <IconButton
          onClick={toggleCart}
          aria-label={texts.closeCart}
          className='orderingCart__container__header__closeButton'
        >
          <X size={20} />
        </IconButton>
      </div>
      <Divider />

      {/* Formulario completo de envío */}
      <div className='orderingCart__container__shippingDetails'>
        <TextField
          label={texts.firstName}
          name='firstName'
          value={shippingDetails.firstName}
          onChange={handleChange}
          fullWidth
          margin='normal'
          error={!!errors?.firstName}
          helperText={errors?.firstName}
        />
        <TextField
          label={texts.lastName}
          name='lastName'
          value={shippingDetails.lastName}
          onChange={handleChange}
          fullWidth
          margin='normal'
          error={!!errors?.lastName}
          helperText={errors?.lastName}
        />
        <TextField
          label={texts.address}
          name='addressLine1'
          value={shippingDetails.addressLine1}
          onChange={handleChange}
          fullWidth
          margin='normal'
          error={!!errors?.addressLine1}
          helperText={errors?.addressLine1}
        />
        <TextField
          label={texts.address2}
          name='addressLine2'
          value={shippingDetails.addressLine2}
          onChange={handleChange}
          fullWidth
          margin='normal'
        />

        {/* Ciudad y Código Postal en la misma línea */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label={texts.city}
              name='city'
              value={shippingDetails.city}
              onChange={handleChange}
              fullWidth
              margin='normal'
              error={!!errors?.city}
              helperText={errors?.city}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={texts.postalCode}
              name='postalCode'
              value={shippingDetails.postalCode}
              onChange={handleChange}
              fullWidth
              margin='normal'
              error={!!errors?.postalCode}
              helperText={errors?.postalCode}
            />
          </Grid>
        </Grid>

        {/* Provincia/Estado y Teléfono en la misma línea */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label={texts.provinceState}
              name='province'
              value={shippingDetails.province}
              onChange={handleChange}
              fullWidth
              margin='normal'
              error={!!errors?.province}
              helperText={errors?.province}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={texts.phone}
              name='phone'
              value={shippingDetails.phone}
              onChange={handleChange}
              fullWidth
              margin='normal'
              error={!!errors?.phone}
              helperText={errors?.phone}
            />
          </Grid>
        </Grid>

        <TextField
          label={texts.additionalInfo}
          name='additionalInfo'
          value={shippingDetails.additionalInfo}
          onChange={handleChange}
          fullWidth
          margin='normal'
          multiline
          rows={3}
        />
      </div>
    </>
  )
}

export default ShippingDetails
